import { useState, useEffect, useRef } from "react";
import liff from "@line/liff";
import { initLiffOnce } from "../context/LiffProfileContext";

// Singleton guard — ป้องกัน double init จาก LiffProfileContext + useLiffAuth
let liffInitPromise = null;

const useLiffAuth = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [liffReady, setLiffReady] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const didInit = useRef(false); // ป้องกัน StrictMode double-invoke

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const initLiff = async () => {
      try {
        // 1️⃣ Init เพียงครั้งเดียว (singleton)
        await initLiffOnce();
        setLiffReady(true);

        // 2️⃣ จัดการ liff.state redirect callback
        const params = new URLSearchParams(window.location.search);
        const liffState = params.get("liff.state");

        if (liffState) {
          // FIX Loop 2: เปรียบ pathname เท่านั้น ไม่ใช้ full href
          const currentPath = window.location.pathname;
          const targetPath = liffState.startsWith("/")
            ? liffState.split("?")[0]
            : (() => {
                try {
                  return new URL(liffState, window.location.origin).pathname;
                } catch {
                  return null;
                }
              })();

          if (targetPath && currentPath !== targetPath) {
            // FIX Loop 2: เก็บ token ก่อน redirect
            if (liff.isLoggedIn()) {
              const token = liff.getAccessToken();
              if (token) {
                localStorage.setItem("authToken", token);
                setLoggedIn(true);
              }
            }
            setIsRedirecting(true);
            window.location.replace(liffState);
            return;
          }

          // URL ตรงกันแล้ว — ทำความสะอาด query params เท่านั้น
          const cleanUrl = window.location.href.replace(
            /[?&]liff\.(state|clientId|redirectUri)=[^&]*/g,
            "",
          );
          if (cleanUrl !== window.location.href) {
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }

        // 3️⃣ ตรวจสอบ login state
        if (!liff.isLoggedIn()) {
          const isInLineApp = /Line/i.test(navigator.userAgent);
          const fullPath =
            window.location.pathname +
            window.location.search +
            window.location.hash;

          if (isInLineApp) {
            setIsRedirecting(true);
            liff.login({ redirectUri: window.location.origin + fullPath });
            return;
          }

          if (process.env.REACT_APP_ENV === "prod") {
            setIsRedirecting(true);
            const lineUrl = `line://app/${process.env.REACT_APP_LIFF_ID}?liff.state=${encodeURIComponent(fullPath)}`;
            window.location.href = lineUrl;

            // Fallback: ถ้า LINE ไม่เปิดภายใน 3 วินาที
            setTimeout(() => {
              if (!document.hidden) {
                alert(
                  "กรุณาเปิดลิงก์นี้ใน LINE เท่านั้น\n\nหรือคลิกที่ลิงก์อีกครั้งจากภายใน LINE",
                );
                setIsRedirecting(false);
              }
            }, 3000);
          } else {
            // Dev mode
            setIsRedirecting(true);
            liff.login({ redirectUri: window.location.origin + fullPath });
          }

          return;
        }

        // 4️⃣ Login สำเร็จ — เก็บ token
        const accessToken = liff.getAccessToken();
        if (accessToken) {
          localStorage.setItem("authToken", accessToken);
          setLoggedIn(true);
        } else {
          console.warn("⚠️ LIFF logged in but no access token");
        }
      } catch (err) {
        console.error("❌ LIFF init failed:", err);
        // Reset singleton เพื่อให้ retry ได้ถ้า init พัง
        liffInitPromise = null;
        setLiffReady(true);
        setIsRedirecting(false);
      }
    };

    initLiff();
  }, []);

  const handleLogin = () => {
    if (!liff.isLoggedIn()) {
      const fullPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      liff.login({ redirectUri: window.location.origin + fullPath });
    }
  };

  const handleLogout = () => {
    if (liff.isLoggedIn()) {
      liff.logout();
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("bigCardNumber");
    // FIX Loop 4: ใช้ navigate แทน reload เพื่อตัด re-init cycle
    setLoggedIn(false);
    setIsRedirecting(false);
    window.location.href = "/";
  };

  return {
    loggedIn,
    liffReady,
    isRedirecting,
    handleLogin,
    handleLogout,
    liff,
  };
};

export default useLiffAuth;
